import { useTranslation } from 'react-i18next';
import { Box, styled } from '@mui/material';
import { TripLegPackage } from '../../../data/models/trip-leg-package.model';
import { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import dateUtils from '../../../services/DateUtils';
import { financialUtils } from '../../../services/financialUtils';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as referenceSlice from '../../../data/reference.slice';

const PREFIX = 'TripPackageSummary';

const classes = {
  content: `${PREFIX}-content`,
  label: `${PREFIX}-label`,
  value: `${PREFIX}-value`,
  countdown: `${PREFIX}-countdown`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.content}`]: {
    backgroundColor: 'rgba(1, 16, 18, 0.04)',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(3),
    borderRadius: 8,
  },
  [`& .${classes.label}`]: {
    fontSize: 12,
    fontWeight: 400,
    textTransform: 'uppercase',
  },
  [`& .${classes.value}`]: {
    fontSize: 18,
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  [`& .${classes.countdown}`]: {
    color: theme.palette.error.main,
  },
}));

interface TripPackageSummaryProps {
  tripPackage: TripLegPackage;
}

export default function TripPackageSummary({
  tripPackage,
}: TripPackageSummaryProps) {
  const { t } = useTranslation('common');

  const tripModes = useAppSelector(referenceSlice.selectTripModes);
  const [countdown, setCountdown] = useState('');

  const serviceStart = useMemo(() => {
    const earliestAppointment = tripPackage.tripLegs.reduce(
      (prevLeg, curLeg) => {
        return curLeg.appointmentDate.localeCompare(prevLeg.appointmentDate) < 0
          ? curLeg
          : prevLeg;
      },
    );
    return earliestAppointment
      ? dateUtils.formatDate(
          DateTime.fromISO(earliestAppointment.appointmentDate),
        )
      : '';
  }, [tripPackage]);

  const tripCount = useMemo(() => {
    return tripPackage.tripLegs.length;
  }, [tripPackage]);

  const tripValue = useMemo(() => {
    return tripPackage.tripLegs.reduce((prevValue, curLeg) => {
      const payableAmount =
        curLeg.tripLegServicePrice?.payableAmount ?? curLeg.payableAmount;
      const amount = payableAmount
        ? financialUtils.parsePrice(payableAmount)
        : 0;
      return prevValue + amount;
    }, 0);
  }, [tripPackage]);

  const tripsScheduled = useMemo(() => {
    return tripPackage.tripLegs.reduce((prevValue, curLeg) => {
      return prevValue + (curLeg.pickUpDueTimeUTC ? 1 : 0);
    }, 0);
  }, [tripPackage]);

  const serviceLevelCounts = useMemo(() => {
    const getIds = (codes: string[]) =>
      tripModes
        .filter((mode) => codes.includes(mode.tripModeBC))
        .map((mode) => mode.tripModeID);

    const AMBI_IDS = getIds(['AMBI']);
    const WC_IDS = getIds(['WC']);
    const GUR_IDS = getIds(['GUR', 'BARGUR']);

    const counts = [
      {
        labelKey: 'notification.ambc2c',
        count: tripPackage.tripLegs.filter(
          (leg) =>
            AMBI_IDS.includes(leg.tripModeID) &&
            leg.passengerCareLevelBC === 'C2C',
        ).length,
      },
      {
        labelKey: 'notification.ambd2d',
        count: tripPackage.tripLegs.filter(
          (leg) =>
            AMBI_IDS.includes(leg.tripModeID) &&
            leg.passengerCareLevelBC === 'D2D',
        ).length,
      },
      {
        labelKey: 'notification.wcd2d',
        count: tripPackage.tripLegs.filter(
          (leg) =>
            WC_IDS.includes(leg.tripModeID) &&
            leg.passengerCareLevelBC === 'D2D',
        ).length,
      },
      {
        labelKey: 'notification.gurr2r',
        count: tripPackage.tripLegs.filter((leg) =>
          GUR_IDS.includes(leg.tripModeID),
        ).length,
      },
    ];

    return counts.filter((c) => c.count > 0);
  }, [tripPackage, tripModes]);

  useEffect(() => {
    const expireTime = DateTime.fromISO(
      tripPackage.tripLegPackageOfferingExpirationDateTimeUTC,
    );

    if (!expireTime.isValid) {
      setCountdown('');
      return;
    }

    let intervalId: ReturnType<typeof setInterval> | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const updateCountdown = () => {
      const remainingMs = expireTime.toMillis() - DateTime.now().toMillis();
      const totalMinutes = Math.max(0, Math.floor(remainingMs / 60000));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      setCountdown(`${hours}:${String(minutes).padStart(2, '0')}`);
    };

    updateCountdown();

    const remainingMs = expireTime.toMillis() - DateTime.now().toMillis();
    if (remainingMs > 0) {
      const msUntilNextMinuteBoundary = remainingMs % 60000 || 60000;
      timeoutId = setTimeout(() => {
        updateCountdown();
        intervalId = setInterval(updateCountdown, 60000);
      }, msUntilNextMinuteBoundary);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [tripPackage.tripLegPackageOfferingExpirationDateTimeUTC]);

  return (
    <StyledBox className={classes.content}>
      <Box component={'div'}>
        <Box component={'div'} className={classes.label}>
          {t('notification.serviceStart')}
        </Box>
        <Box component={'div'} className={classes.value}>
          {serviceStart}
        </Box>
      </Box>
      <Box component={'div'}>
        <Box component={'div'} className={classes.label}>
          {t('notification.trips')}
        </Box>
        <Box component={'div'} className={classes.value}>
          {tripCount}
        </Box>
      </Box>
      <Box component={'div'}>
        <Box component={'div'} className={classes.label}>
          {t('notification.value')}
        </Box>
        <Box component={'div'} className={classes.value}>
          {financialUtils.formatPrice(tripValue)}
        </Box>
      </Box>
      {serviceLevelCounts.map(({ labelKey, count }) => (
        <Box component={'div'} key={labelKey}>
          <Box component={'div'} className={classes.label}>
            {t(labelKey)}
          </Box>
          <Box component={'div'} className={classes.value}>
            {count}
          </Box>
        </Box>
      ))}
      {tripsScheduled > 0 && (
        <Box component={'div'}>
          <Box component={'div'} className={classes.label}>
            {t('notification.scheduled')}
          </Box>
          <Box component={'div'} className={classes.value}>
            {tripsScheduled}
          </Box>
        </Box>
      )}
      {tripCount - tripsScheduled > 0 && (
        <Box component={'div'}>
          <Box component={'div'} className={classes.label}>
            {t('notification.willCall')}
          </Box>
          <Box component={'div'} className={classes.value}>
            {tripCount - tripsScheduled}
          </Box>
        </Box>
      )}
      <Box component={'div'}>
        <Box component={'div'} className={classes.label}>
          {t('notification.expires')}
        </Box>
        <Box
          component={'div'}
          className={`${classes.value} ${classes.countdown}`}>
          {countdown}
        </Box>
      </Box>
    </StyledBox>
  );
}
